import { synchronizeBambooGift, isBambooGiftOfferActive } from './skin-elite-bamboo-gift.js?v=c0f519abaf276c68';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { getScrollContainer } from '@theme/scroll-container';

const initialJudgeMeReviewCount = 15;

let selectedVariantId;
let variantMap;
let selectedQuantity = 4;
let sizeDialogReturnFocus;
let activeProductSection;
let productSectionObserver;
let productUpdateFrame;
let variantSyncFrame;
let stickyVisibilityFrame;
let bundleAddInProgress = false;
let initializedOfferSelector;
let initializedBalmToggle;
let balmUpsellSelected = false;
let sizeDialogTargetSelect;
const variantSyncTimeouts = new Set();
const primaryMediaRestoreTimeouts = new Set();
const variantMediaFocusTimeouts = new Set();
let primaryMediaRestoreScheduled = false;
let reviewWidgetObserver;
let reviewWidgetFrame;
let reviewLoadStartCount;
let reviewLoadFallbackTimeout;
let hashScrollFrame;
let hashScrollTimeout;

const compressionLandingHashTargets = new Set(['compression-offer', 'skin-elite-reviews']);

function scrollToCompressionLandingHash() {
  const targetId = window.location.hash.slice(1);
  if (!compressionLandingHashTargets.has(targetId)) return;

  const target = document.getElementById(targetId);
  if (!(target instanceof HTMLElement)) return;

  const scrollContainer = getScrollContainer();
  const isDocumentScroller =
    scrollContainer === document.scrollingElement ||
    scrollContainer === document.documentElement ||
    scrollContainer === document.body;
  const containerTop = isDocumentScroller ? 0 : scrollContainer.getBoundingClientRect().top;
  const headerHeight = document.querySelector('#header-group')?.getBoundingClientRect().height || 0;
  const targetTop = scrollContainer.scrollTop + target.getBoundingClientRect().top - containerTop;

  scrollContainer.scrollTo({
    top: Math.max(0, targetTop - headerHeight - 12),
    behavior: 'instant',
  });
}

function scheduleCompressionLandingHashScroll() {
  if (!window.location.hash) return;

  if (hashScrollFrame) window.cancelAnimationFrame(hashScrollFrame);
  if (hashScrollTimeout) window.clearTimeout(hashScrollTimeout);

  hashScrollFrame = window.requestAnimationFrame(() => {
    hashScrollFrame = undefined;
    scrollToCompressionLandingHash();
  });
  hashScrollTimeout = window.setTimeout(() => {
    hashScrollTimeout = undefined;
    scrollToCompressionLandingHash();
  }, 350);
}

function getMainContent() {
  return document.querySelector('#MainContent');
}

function getReviewWidget() {
  return getMainContent()?.querySelector('#judgeme_product_reviews');
}

function getReviewSlot() {
  return getMainContent()?.querySelector('[data-compression-review-slot]');
}

function clearReviewLoadRequest() {
  reviewLoadStartCount = undefined;
  if (reviewLoadFallbackTimeout) window.clearTimeout(reviewLoadFallbackTimeout);
  reviewLoadFallbackTimeout = undefined;
}

function syncInitialReviewCount() {
  const reviewWidget = getReviewWidget();
  const reviewSlot = getReviewSlot();
  if (!reviewWidget || !reviewSlot) return;

  const reviewCount = reviewWidget.querySelectorAll('.jdgm-review-card').length;
  if (reviewLoadStartCount !== undefined) {
    if (reviewCount <= reviewLoadStartCount) return;
    clearReviewLoadRequest();
    window.setTimeout(scheduleReviewWidgetSync, 400);
    return;
  }

  if (reviewCount >= initialJudgeMeReviewCount) return;

  const showMoreButton = reviewWidget.querySelector('.jdgm-masonry-grid__btn');
  if (!(showMoreButton instanceof HTMLButtonElement) || showMoreButton.disabled) return;

  reviewLoadStartCount = reviewCount;
  showMoreButton.click();

  const requestedCount = reviewCount;
  reviewLoadFallbackTimeout = window.setTimeout(() => {
    if (reviewLoadStartCount !== requestedCount) return;
    clearReviewLoadRequest();
    scheduleReviewWidgetSync();
  }, 8000);
}

function scheduleReviewWidgetSync() {
  if (reviewWidgetFrame) return;

  reviewWidgetFrame = requestAnimationFrame(() => {
    reviewWidgetFrame = undefined;
    syncInitialReviewCount();
  });
}

function observeReviewWidget() {
  if (reviewWidgetObserver) return;

  const mainContent = getMainContent();
  if (!mainContent) return;

  reviewWidgetObserver = new MutationObserver(scheduleReviewWidgetSync);
  reviewWidgetObserver.observe(mainContent, { childList: true, subtree: true });
  scheduleReviewWidgetSync();
}

function teardownReviewWidget() {
  reviewWidgetObserver?.disconnect();
  reviewWidgetObserver = undefined;
  if (reviewWidgetFrame) cancelAnimationFrame(reviewWidgetFrame);
  reviewWidgetFrame = undefined;
  clearReviewLoadRequest();
}

function findProductSection(scope = getMainContent()) {
  if (!scope?.querySelector) return undefined;

  const productAnchor = scope.matches?.('[data-compression-offer-selector]')
    ? scope
    : scope.querySelector('[data-compression-offer-selector]');

  return productAnchor?.closest('.shopify-section');
}

function getProductSection() {
  if (activeProductSection?.isConnected) return activeProductSection;
  return findProductSection();
}

function getVariantMap() {
  if (variantMap) return variantMap;

  const variantMapJson = getProductSection()?.querySelector('[data-compression-variant-map]');
  if (!variantMapJson?.textContent) return [];

  try {
    variantMap = JSON.parse(variantMapJson.textContent);
  } catch {
    variantMap = [];
  }

  return variantMap;
}

function getSelectedOptionValues() {
  const variantPicker = getProductSection()?.querySelector('variant-picker');
  if (!variantPicker) return [];

  const values = Array.from(variantPicker.querySelectorAll('select')).map((select) => select.value);
  variantPicker.querySelectorAll('fieldset').forEach((fieldset) => {
    const selectedInput = fieldset.querySelector('input:checked');
    if (selectedInput instanceof HTMLInputElement) values.push(selectedInput.value);
  });

  return values;
}

function resolveSelectedVariant() {
  const selectedOptions = getSelectedOptionValues();
  if (!selectedOptions.length) return undefined;

  return getVariantMap().find(
    (item) =>
      item.options?.length === selectedOptions.length &&
      item.options.every((option, index) => option === selectedOptions[index])
  );
}

function getCommerceVariantId(variant) {
  if (!variant) return undefined;
  if (Object.prototype.hasOwnProperty.call(variant, 'commerceId')) {
    return variant.commerceId || undefined;
  }

  return variant.id || undefined;
}

function resolveSelectedVariantId() {
  return getCommerceVariantId(resolveSelectedVariant());
}

function setFormVariantId(form, variantId) {
  if (!(form instanceof HTMLFormElement)) return;

  const currentInput = form.querySelector('input[name="id"]');
  const nextVariantId = String(variantId);

  if (currentInput instanceof HTMLInputElement) {
    if (currentInput.type !== 'hidden') currentInput.type = 'hidden';
    if (currentInput.value !== nextVariantId) currentInput.value = nextVariantId;
    if (currentInput.getAttribute('value') !== nextVariantId) currentInput.setAttribute('value', nextVariantId);
    if (currentInput.getAttribute('ref') !== 'variantId') currentInput.setAttribute('ref', 'variantId');
    return;
  }

  const variantInput = document.createElement('input');
  variantInput.type = 'hidden';
  variantInput.name = 'id';
  variantInput.value = nextVariantId;
  variantInput.setAttribute('value', nextVariantId);
  variantInput.setAttribute('ref', 'variantId');

  form.prepend(variantInput);
}

function setProductFormVariantId(variantId, form) {
  if (!variantId) return;

  if (form) {
    setFormVariantId(form, variantId);
    return;
  }

  getProductSection()
    ?.querySelectorAll('product-form-component form[data-type="add-to-cart-form"]')
    .forEach((productForm) => setFormVariantId(productForm, variantId));
}

function readRenderedVariantId() {
  const variantJson = getProductSection()?.querySelector('variant-picker script[type="application/json"]');
  if (!variantJson?.textContent) return undefined;

  try {
    return JSON.parse(variantJson.textContent)?.id || undefined;
  } catch {
    return undefined;
  }
}

function syncInitialProductVariant() {
  const offerSelector = getOfferSelector();
  const isCommerceProduct =
    String(offerSelector?.dataset.productId || '') === String(offerSelector?.dataset.commerceProductId || '');
  const renderedVariantId = isCommerceProduct ? readRenderedVariantId() : undefined;
  const resolvedVariantId = resolveSelectedVariantId() || renderedVariantId || selectedVariantId;

  if (resolvedVariantId) {
    selectedVariantId = resolvedVariantId;
    setProductFormVariantId(resolvedVariantId);
    return;
  }

  // Keep Horizon's native product form state if the rendered JSON is unavailable.
}

function syncResolvedVariantState() {
  const resolvedVariant = resolveSelectedVariant();
  const commerceVariantId = getCommerceVariantId(resolvedVariant);
  if (commerceVariantId) {
    selectedVariantId = commerceVariantId;
    syncUntouchedBundleRowsFromVariant(resolvedVariant);
  }

  syncInitialProductVariant();
  syncOfferSelector();
}

function scheduleVariantSync() {
  if (variantSyncFrame) cancelAnimationFrame(variantSyncFrame);
  variantSyncTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
  variantSyncTimeouts.clear();

  variantSyncFrame = requestAnimationFrame(() => {
    variantSyncFrame = undefined;
    syncResolvedVariantState();
  });

  [80, 300, 1000].forEach((delay) => {
    const timeoutId = window.setTimeout(() => {
      variantSyncTimeouts.delete(timeoutId);
      syncResolvedVariantState();
    }, delay);
    variantSyncTimeouts.add(timeoutId);
  });
}

function scheduleInitialPrimaryMediaRestore() {
  if (primaryMediaRestoreScheduled) return;
  primaryMediaRestoreScheduled = true;

  [0, 120, 500].forEach((delay) => {
    const timeoutId = window.setTimeout(() => {
      primaryMediaRestoreTimeouts.delete(timeoutId);

      const offerSelector = getOfferSelector();
      if (!offerSelector || offerSelector.dataset.bundleDirty === 'true') return;

      const gallery = getProductSection()?.querySelector('media-gallery');
      gallery?.slideshow?.select?.(0, undefined, { animate: false });
    }, delay);

    primaryMediaRestoreTimeouts.add(timeoutId);
  });
}

function findVariantMediaThumbnail(color) {
  const normalizedColor = String(color).trim().toLowerCase();
  const expectedSource = `variant-${normalizedColor}-bg-preview`;
  const expectedAlts = [
    `skin-elite bamboo compression socks in ${normalizedColor}, worn by a woman`,
    `skin-elite bamboo compression socks in ${normalizedColor}, front view`,
  ];
  const thumbnails = Array.from(
    getProductSection()?.querySelectorAll('media-gallery .slideshow-controls__thumbnail') || []
  );

  for (const expectedAlt of expectedAlts) {
    const matchingThumbnail = thumbnails.find((thumbnail) => {
      const image = thumbnail.querySelector('img');
      const alt = image?.alt?.trim().toLowerCase();
      const source = image?.currentSrc || image?.src || '';
      return alt?.includes(expectedAlt) || source.toLowerCase().includes(expectedSource);
    });
    if (matchingThumbnail) return matchingThumbnail;
  }

  return undefined;
}

function scheduleVariantMediaFocus(color) {
  variantMediaFocusTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
  variantMediaFocusTimeouts.clear();

  [0, 160, 600].forEach((delay) => {
    const timeoutId = window.setTimeout(() => {
      variantMediaFocusTimeouts.delete(timeoutId);
      findVariantMediaThumbnail(color)?.click();
    }, delay);

    variantMediaFocusTimeouts.add(timeoutId);
  });
}

function syncSelectedProductVariant(event) {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
  if (event.target.closest('.shopify-section') !== getProductSection()) return;

  const offerSelector = getOfferSelector();
  const isCommerceProduct =
    String(offerSelector?.dataset.productId || '') === String(offerSelector?.dataset.commerceProductId || '');
  const requestedVariantId = resolveSelectedVariantId() || (isCommerceProduct ? event.detail?.variantId : undefined);
  selectedVariantId = requestedVariantId || undefined;
  syncUntouchedBundleRowsFromVariant(getSelectedVariantData());
  setProductFormVariantId(selectedVariantId);
  syncOfferSelector();
  scheduleVariantSync();

  event.promise
    ?.then(({ detail }) => {
      selectedVariantId =
        resolveSelectedVariantId() ||
        (isCommerceProduct ? detail?.resource?.id : undefined) ||
        requestedVariantId ||
        undefined;
      syncUntouchedBundleRowsFromVariant(getSelectedVariantData());
      setProductFormVariantId(selectedVariantId);
      syncOfferSelector();
      scheduleVariantSync();
    })
    .catch(() => {});
}

function syncVariantControlChange(event) {
  if (!(event.target instanceof Element) || !event.target.closest('variant-picker')) return;
  if (event.target.closest('.shopify-section') !== getProductSection()) return;

  selectedVariantId = resolveSelectedVariantId() || selectedVariantId;
  scheduleVariantSync();
}

function handleBundleProductSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-type="add-to-cart-form"]')) return;
  if (form.closest('.shopify-section') !== getProductSection()) return;
  if (!getOfferSelector()) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  void addSelectedBundleToCart(form);
}

function handleBundleAddClick(event) {
  if (!(event.target instanceof Element) || !event.target.closest('.add-to-cart-button')) return;

  const form = event.target.closest('form[data-type="add-to-cart-form"]');
  if (!(form instanceof HTMLFormElement) || form.closest('.shopify-section') !== getProductSection()) return;
  if (!getOfferSelector()) return;

  // Horizon's delegated click handler has already run its button animation. Cancel
  // the browser's default submit and run one JSON batch request instead.
  event.preventDefault();
  event.stopImmediatePropagation();
  void addSelectedBundleToCart(form);
}

function friendlyStyleText(text) {
  return text.trim();
}

function applyFriendlyVariantLabels() {
  const productSection = getProductSection();
  if (!productSection) return;

  productSection
    .querySelectorAll('.variant-option__select option, [data-compression-pair-color] option')
    .forEach((option) => {
      const friendlyText = friendlyStyleText(option.value);
      const unavailableSuffix = option.textContent.includes(' - ')
        ? option.textContent.slice(option.textContent.indexOf(' - '))
        : '';
      const nextText = `${friendlyText}${unavailableSuffix}`;

      if (option.textContent.trim() !== nextText) option.textContent = nextText;
    });

  productSection.querySelectorAll('[data-key="variant-option-text"]').forEach((label) => {
    const nextText = friendlyStyleText(label.textContent);
    if (label.textContent.trim() !== nextText) label.textContent = nextText;
  });

  productSection.querySelectorAll('[data-testid="sticky-variant-title"]').forEach((title) => {
    const nextText = friendlyStyleText(title.textContent);
    if (title.textContent.trim() !== nextText) title.textContent = nextText;
  });
}

function getOfferSelector() {
  return getProductSection()?.querySelector('[data-compression-offer-selector]');
}

function getCommerceProductId() {
  const offerSelector = getOfferSelector();
  if (!offerSelector) return undefined;
  if (Object.prototype.hasOwnProperty.call(offerSelector.dataset, 'commerceProductId')) {
    return offerSelector.dataset.commerceProductId || undefined;
  }

  return offerSelector.dataset.productId || undefined;
}

function getSelectedVariantData() {
  const resolvedVariant = resolveSelectedVariant();
  if (!selectedVariantId) return resolvedVariant;

  return (
    getVariantMap().find(
      (variant) => String(getCommerceVariantId(variant)) === String(selectedVariantId)
    ) || resolvedVariant
  );
}

function setTextContent(element, text) {
  if (element && element.textContent.trim() !== text) element.textContent = text;
}

function getPairSelects(row) {
  if (!(row instanceof HTMLElement)) return [];
  return Array.from(row.querySelectorAll('[data-compression-pair-option]')).filter(
    (select) => select instanceof HTMLSelectElement
  );
}

function syncColorPicker(select) {
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-compression-pair-color]')) return;

  const picker = select.closest('[data-compression-color-picker]');
  if (!(picker instanceof HTMLElement)) return;

  const color = select.value;
  const swatch = picker.querySelector('[data-compression-color-swatch]');
  const label = picker.querySelector('[data-compression-color-label]');
  const trigger = picker.querySelector('[data-compression-color-trigger]');

  if (swatch instanceof HTMLElement) swatch.dataset.colorValue = color;
  setTextContent(label, color);
  trigger?.setAttribute('aria-label', `Colour: ${color}`);

  picker.querySelectorAll('[data-compression-color-choice]').forEach((choice) => {
    if (!(choice instanceof HTMLButtonElement)) return;

    const isSelected = choice.dataset.compressionColorChoice === color;
    const nativeOption = Array.from(select.options).find(
      (option) => option.value === choice.dataset.compressionColorChoice
    );

    choice.setAttribute('aria-selected', String(isSelected));
    choice.disabled = Boolean(nativeOption?.disabled);
  });
}

function closeColorPickers(exceptPicker) {
  getProductSection()?.querySelectorAll('[data-compression-color-picker]').forEach((picker) => {
    if (!(picker instanceof HTMLElement) || picker === exceptPicker) return;

    const trigger = picker.querySelector('[data-compression-color-trigger]');
    const menu = picker.querySelector('[data-compression-color-menu]');
    trigger?.setAttribute('aria-expanded', 'false');
    if (menu instanceof HTMLElement) menu.hidden = true;
  });
}

function handleColorPickerClick(event) {
  if (!(event.target instanceof Element)) return;

  const choice = event.target.closest('[data-compression-color-choice]');
  if (choice instanceof HTMLButtonElement) {
    const picker = choice.closest('[data-compression-color-picker]');
    const select = picker?.querySelector('[data-compression-pair-color]');
    const color = choice.dataset.compressionColorChoice;
    if (!(select instanceof HTMLSelectElement) || !color || choice.disabled) return;

    select.value = color;
    syncColorPicker(select);
    closeColorPickers();
    picker?.querySelector('[data-compression-color-trigger]')?.focus();
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  const trigger = event.target.closest('[data-compression-color-trigger]');
  if (trigger instanceof HTMLButtonElement) {
    const picker = trigger.closest('[data-compression-color-picker]');
    const menu = picker?.querySelector('[data-compression-color-menu]');
    if (!(picker instanceof HTMLElement) || !(menu instanceof HTMLElement)) return;

    const shouldOpen = menu.hidden;
    closeColorPickers(shouldOpen ? picker : undefined);
    menu.hidden = !shouldOpen;
    trigger.setAttribute('aria-expanded', String(shouldOpen));
    return;
  }

  if (!event.target.closest('[data-compression-color-picker]')) closeColorPickers();
}

function handleColorPickerKeydown(event) {
  if (event.key !== 'Escape' || !(event.target instanceof Element)) return;

  const picker = event.target.closest('[data-compression-color-picker]');
  if (!(picker instanceof HTMLElement)) return;

  event.preventDefault();
  closeColorPickers();
  picker.querySelector('[data-compression-color-trigger]')?.focus();
}

function getPairOptionValues(row) {
  const variants = getVariantMap();
  const fallbackOptions = getSelectedVariantData()?.options || variants[0]?.options || [];
  const optionValues = [...fallbackOptions];

  getPairSelects(row).forEach((select) => {
    const optionPosition = Number(select.dataset.optionPosition);
    if (Number.isInteger(optionPosition) && optionPosition > 0) {
      optionValues[optionPosition - 1] = select.value;
    }
  });

  return optionValues;
}

function findVariantByOptions(optionValues) {
  if (!optionValues.length || optionValues.some((value) => value === undefined || value === '')) return undefined;

  return getVariantMap().find(
    (variant) =>
      variant.options?.length === optionValues.length &&
      variant.options.every((option, index) => String(option) === String(optionValues[index]))
  );
}

function resolvePairVariant(row) {
  return findVariantByOptions(getPairOptionValues(row));
}

function syncPrimaryPairToNativeVariant(row) {
  if (!(row instanceof HTMLElement) || row.dataset.pairIndex !== '0') return;
  if (row.closest('[data-compression-bundle]') !== getActiveBundle()) return;

  const variant = resolvePairVariant(row);
  const variantPicker = getProductSection()?.querySelector('variant-picker');

  if (
    !variant?.id ||
    !(variantPicker instanceof HTMLElement) ||
    typeof variantPicker.updateSelectedOption !== 'function'
  ) {
    return;
  }

  const selects = Array.from(variantPicker.querySelectorAll('select')).filter(
    (select) => select instanceof HTMLSelectElement
  );

  if (selects.length !== variant.options.length) return;

  const targets = selects.map((select, index) => {
    const option = Array.from(select.options).find(
      (item) => item.value === String(variant.options[index])
    );

    return option ? { select, option } : undefined;
  });

  if (targets.some((target) => !target)) return;

  let changedSelect;

  targets.forEach(({ select, option }) => {
    if (select.value !== option.value) changedSelect = select;
    select.value = option.value;
    variantPicker.updateSelectedOption(select);
  });

  if (!changedSelect) return;

  const selectedOption = changedSelect.selectedOptions[0];
  if (selectedOption) selectedOption.dataset.variantId = String(variant.id);

  changedSelect.dispatchEvent(new Event('change', { bubbles: true }));
}

function syncInitialOfferVariantToNative() {
  const offerSelector = getOfferSelector();
  if (!offerSelector || offerSelector.dataset.initialVariantSynced === 'true') return;

  offerSelector.dataset.initialVariantSynced = 'true';
  const firstPair = getActiveBundle(offerSelector)?.querySelector('[data-compression-pair][data-pair-index="0"]');
  syncPrimaryPairToNativeVariant(firstPair);
}

function setPairSelectionsFromVariant(row, variant) {
  if (!(row instanceof HTMLElement) || !variant?.options?.length || row.dataset.userSelected === 'true') return;

  getPairSelects(row).forEach((select) => {
    const optionPosition = Number(select.dataset.optionPosition);
    const optionValue = variant.options[optionPosition - 1];
    if (optionValue === undefined) return;

    if (Array.from(select.options).some((option) => option.value === String(optionValue))) {
      select.value = String(optionValue);
      syncColorPicker(select);
    }
  });
}

function syncUntouchedBundleRowsFromVariant(variant) {
  const offerSelector = getOfferSelector();
  // Once a shopper edits a pair, native variant updates must not copy that
  // pair's choices over the other rows in a mix-and-match bundle.
  if (!offerSelector || !variant || offerSelector.dataset.bundleDirty === 'true') return;

  offerSelector.querySelectorAll('[data-compression-pair]').forEach((row) => {
    setPairSelectionsFromVariant(row, variant);
  });
}

function syncPairOptionAvailability(row) {
  getPairSelects(row).forEach((select) => {
    const optionPosition = Number(select.dataset.optionPosition);
    if (!Number.isInteger(optionPosition) || optionPosition < 1) return;

    Array.from(select.options).forEach((option) => {
      const hasAvailableVariant = getVariantMap().some(
        (variant) => variant.available && String(variant.options?.[optionPosition - 1]) === option.value
      );

      // Disable an option only when it is unavailable across the whole product.
      // Combination-level disabling can deadlock two selects (A/X → B/Y), so an
      // unavailable pair is instead reported by the row status and validation.
      option.disabled = option.value !== select.value && !hasAvailableVariant;
    });
  });
}

function syncPairRow(row) {
  if (!(row instanceof HTMLElement)) return undefined;

  syncPairOptionAvailability(row);
  getPairSelects(row).forEach((select) => syncColorPicker(select));
  const variant = resolvePairVariant(row);
  const commerceVariantId = getCommerceVariantId(variant);
  const isAvailable = Boolean(commerceVariantId && variant?.available);
  const status = row.querySelector('[data-compression-pair-status]');
  const variantInput = row.querySelector('[data-compression-pair-variant-id]');
  const selects = getPairSelects(row);
  const firstSelect = selects[0];
  const statusId = status?.id || (firstSelect?.id ? `${firstSelect.id}-Status` : '');

  if (status && statusId && !status.id) status.id = statusId;
  if (variantInput instanceof HTMLInputElement) {
    const resolvedVariantId = commerceVariantId ? String(commerceVariantId) : '';
    variantInput.value = resolvedVariantId;
    variantInput.setAttribute('value', resolvedVariantId);
  }

  row.dataset.variantId = commerceVariantId ? String(commerceVariantId) : '';
  row.dataset.available = String(isAvailable);
  row.classList.toggle('is-unavailable', !isAvailable);

  const statusText = variant ? (isAvailable ? '' : 'Sold out') : 'Combination unavailable';
  if (status) status.toggleAttribute('hidden', isAvailable);
  setTextContent(status, statusText);

  selects.forEach((select) => {
    select.setAttribute('aria-invalid', String(!isAvailable));
    if (statusId) {
      const describedBy = new Set((select.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      if (isAvailable) describedBy.delete(statusId);
      else describedBy.add(statusId);

      if (describedBy.size > 0) select.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
      else select.removeAttribute('aria-describedby');
    }
  });

  return variant;
}

function formatMoney(cents, offerSelector = getOfferSelector()) {
  const currencyCode = offerSelector?.dataset.currencyCode || 'USD';
  const locale = document.documentElement.lang || 'en';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currencyCode}`;
  }
}

function getStickyBundleVariantLabel(bundle) {
  if (!(bundle instanceof HTMLElement)) return '';

  const pairLabels = Array.from(bundle.querySelectorAll('[data-compression-pair]'))
    .map((row) => getPairSelects(row).map((select) => select.value).filter(Boolean).join(' / '))
    .filter(Boolean);

  if (!pairLabels.length) return '';

  const quantity = Number(bundle.dataset.bundleQuantity) || pairLabels.length;
  const allPairsMatch = pairLabels.every((label) => label === pairLabels[0]);
  const selectionLabel = allPairsMatch ? pairLabels[0] : 'Mix & Match';
  const pairLabel = quantity === 1 ? 'Pair' : 'Pairs';

  return `${quantity} ${pairLabel} · ${selectionLabel}`;
}

function syncStickyBundleSummary(activeState, offerSelector = getOfferSelector()) {
  const stickyCart = getProductSection()?.querySelector('sticky-add-to-cart');
  if (!(stickyCart instanceof HTMLElement) || !(activeState.bundle instanceof HTMLElement)) return;

  const variantTitle = stickyCart.querySelector('[data-testid="sticky-variant-title"]');
  const priceDisplay = stickyCart.querySelector('[data-testid="sticky-price-display"]');
  const productImage = stickyCart.querySelector('[data-testid="sticky-product-image"]');
  const firstVariant = activeState.variants[0];
  const formattedTotal = activeState.total > 0 ? formatMoney(activeState.total, offerSelector) : 'Choose options';

  setTextContent(variantTitle, `${getStickyBundleVariantLabel(activeState.bundle)}${activeState.balmUpsell?.selected ? ' + Miracle Balm' : ''}`);
  setTextContent(priceDisplay, formattedTotal);

  stickyCart.dataset.variantAvailable = String(activeState.isAvailable);
  if (firstVariant?.id) stickyCart.dataset.currentVariantId = String(firstVariant.id);

  if (productImage instanceof HTMLImageElement && firstVariant?.featuredImage?.url) {
    productImage.src = firstVariant.featuredImage.url;
    productImage.alt = firstVariant.featuredImage.alt || 'SKIN ELITE CO. Bamboo Compression Socks';
  }
}

function syncMainBundleButtonPrice(activeState, offerSelector = getOfferSelector()) {
  const priceSuffix = getProductSection()?.querySelector('[data-compression-atc-price]');
  if (!(priceSuffix instanceof HTMLElement)) return;

  const canShowPrice =
    activeState.isAvailable && Number.isFinite(activeState.total) && activeState.total > 0;

  const priceSeparator = offerSelector?.dataset.productId === '9585046782189' ? '-' : '—';
  const currentPrice = priceSuffix.querySelector('[data-compression-atc-current]');
  const comparePrice = priceSuffix.querySelector('[data-compression-atc-compare]');
  setTextContent(currentPrice || priceSuffix, canShowPrice ? `${priceSeparator} ${formatMoney(activeState.total, offerSelector)}` : '');
  if (comparePrice) {
    const compareTotal = Number(activeState.compareTotal);
    const showCompare = canShowPrice && Number.isFinite(compareTotal) && compareTotal > activeState.total;
    setTextContent(comparePrice, showCompare ? formatMoney(compareTotal, offerSelector) : '');
    comparePrice.hidden = !showCompare;
  }
  priceSuffix.toggleAttribute('hidden', !canShowPrice);
}

function resolveBundleGift(picker) {
  if (!picker) return undefined;
  const values = [];
  picker.querySelectorAll('[data-compression-gift-option]').forEach((select) => {
    values[Number(select.dataset.optionPosition) - 1] = select.value;
  });
  return findVariantByOptions(values);
}

function syncBundlePanel(bundle) {
  if (!(bundle instanceof HTMLElement)) return { isAvailable: false, variants: [], total: 0 };

  const variants = Array.from(bundle.querySelectorAll('[data-compression-pair]')).map((row) => syncPairRow(row));
  const hasResolvedVariants = variants.length > 0 && variants.every(Boolean);
  const giftOfferActive = isBambooGiftOfferActive();
  bundle.querySelectorAll('[data-compression-gift-group]').forEach((group) => {
    group.hidden = !giftOfferActive;
    group.querySelectorAll('[data-compression-gift-option]').forEach((select) => {
      select.disabled = !giftOfferActive;
    });
  });
  const giftPickers = giftOfferActive ? Array.from(bundle.querySelectorAll('[data-compression-gift]')) : [];
  const giftAvailable = giftPickers.map((picker) => {
    const variant = resolveBundleGift(picker);
    const available = Boolean(getCommerceVariantId(variant) && variant.available);
    const status = picker.querySelector('[data-compression-gift-status]');
    if (status) {
      status.hidden = available;
      setTextContent(status, available ? '' : 'This gift size and colour is unavailable. Please choose another.');
    }
    return available;
  }).every(Boolean);
  const isAvailable =
    hasResolvedVariants && giftAvailable &&
    variants.every((variant) => Boolean(getCommerceVariantId(variant) && variant.available));
  const configuredTotal = Number(bundle.dataset.bundleTotal);
  const total = hasResolvedVariants
    ? Number.isFinite(configuredTotal) && configuredTotal > 0
      ? configuredTotal
      : variants.reduce((sum, variant) => sum + (Number(variant.price) || 0), 0)
    : 0;
  const totalElement = bundle.querySelector('[data-compression-total]');

  bundle.dataset.available = String(isAvailable);
  bundle.classList.toggle('is-unavailable', !isAvailable);

  setTextContent(totalElement, hasResolvedVariants ? formatMoney(total) : 'Choose options');

  const configuredCompareTotal = Number(bundle.dataset.bundleCompareTotal);
  const compareTotal = Number.isFinite(configuredCompareTotal) && configuredCompareTotal > total
    ? configuredCompareTotal : total;
  return { isAvailable, variants, total, compareTotal };
}

function getActiveBundle(offerSelector = getOfferSelector()) {
  if (!offerSelector) return undefined;
  return offerSelector.querySelector(`[data-compression-bundle][data-bundle-quantity="${selectedQuantity}"]`);
}

function getBalmUpsellState(offerSelector = getOfferSelector()) {
  const card = offerSelector?.querySelector('[data-compression-balm-upsell]');
  const toggle = card?.querySelector('[data-compression-balm-toggle]');
  if (!(card instanceof HTMLElement) || !(toggle instanceof HTMLInputElement)) return { selected: false, price: 0 };
  const variantId = String(card.dataset.variantId || '');
  const price = Number(card.dataset.price);
  const configuredComparePrice = Number(card.dataset.compareAtPrice);
  const comparePrice = Number.isFinite(configuredComparePrice) && configuredComparePrice > price
    ? configuredComparePrice : price;
  const available = card.dataset.available === 'true' && !toggle.disabled && /^[1-9]\d*$/.test(variantId) && Number.isFinite(price) && price > 0;
  if (initializedBalmToggle !== toggle) {
    toggle.checked = balmUpsellSelected && available;
    initializedBalmToggle = toggle;
  }
  if (!available) {
    toggle.checked = false;
    balmUpsellSelected = false;
  }
  return { selected: available && toggle.checked, price: available ? price : 0, comparePrice: available ? comparePrice : 0, variantId, sourceVariantId: String(card.dataset.sourceVariantId || '') };
}

function getActiveBundleState(offerSelector = getOfferSelector()) {
  const bundle = getActiveBundle(offerSelector);
  if (!(bundle instanceof HTMLElement)) return { bundle: undefined, isAvailable: false, variants: [], total: 0 };
  const state = syncBundlePanel(bundle);
  const balmUpsell = getBalmUpsellState(offerSelector);
  return {
    bundle, ...state, balmUpsell,
    total: state.total + (balmUpsell.selected ? balmUpsell.price : 0),
    compareTotal: state.compareTotal + (balmUpsell.selected ? balmUpsell.comparePrice : 0),
  };
}

function setBundleAddButtonState(isAvailable) {
  const productSection = getProductSection();
  if (!productSection) return;

  productSection.querySelectorAll('.add-to-cart-button').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = bundleAddInProgress || !isAvailable;
    button.setAttribute('aria-disabled', String(bundleAddInProgress || !isAvailable));
    button.setAttribute('aria-busy', String(bundleAddInProgress));
  });
}

function setBundleLiveStatus(message, isError = false) {
  const offerSelector = getOfferSelector();
  const status = offerSelector?.querySelector('[data-compression-bundle-status]');
  if (!status) return;

  status.setAttribute('aria-live', isError ? 'assertive' : 'polite');
  setTextContent(status, message);
}

function syncOfferSelector({ announce = false } = {}) {
  const offerSelector = getOfferSelector();
  if (!offerSelector) return;

  const options = Array.from(offerSelector.querySelectorAll('[data-compression-quantity]'));
  const availableQuantities = options
    .map((option) => Number(option.getAttribute('data-compression-quantity')))
    .filter((quantity) => Number.isInteger(quantity) && quantity > 0);

  if (initializedOfferSelector !== offerSelector) {
    const initialQuantity = Number(offerSelector.dataset.selectedQuantity);
    if (availableQuantities.includes(initialQuantity)) selectedQuantity = initialQuantity;
    initializedOfferSelector = offerSelector;
  }

  if (!availableQuantities.includes(selectedQuantity)) selectedQuantity = availableQuantities[0] || 1;

  offerSelector.dataset.selectedQuantity = String(selectedQuantity);

  options.forEach((option) => {
    if (!(option instanceof HTMLElement)) return;

    const quantity = Number(option.dataset.compressionQuantity);
    const isSelected = quantity === selectedQuantity;
    const input = option.querySelector('[data-compression-quantity-input]');

    option.classList.toggle('is-selected', isSelected);
    if (input instanceof HTMLInputElement) {
      input.checked = isSelected;
      input.disabled = false;
      input.setAttribute('aria-expanded', String(isSelected));
    }
  });

  offerSelector.querySelectorAll('[data-compression-bundle]').forEach((bundle) => {
    if (!(bundle instanceof HTMLElement)) return;
    const isSelected = Number(bundle.dataset.bundleQuantity) === selectedQuantity;
    bundle.classList.toggle('is-selected', isSelected);
    syncBundlePanel(bundle);
  });

  offerSelector.querySelectorAll('[data-compression-bundle-panel]').forEach((panel) => {
    if (!(panel instanceof HTMLElement)) return;
    const isSelected = Number(panel.dataset.compressionBundlePanel) === selectedQuantity;
    panel.hidden = !isSelected;
    panel.setAttribute('aria-hidden', String(!isSelected));
  });

  const activeState = getActiveBundleState(offerSelector);
  const firstVariant = activeState.variants[0];
  const firstCommerceVariantId = getCommerceVariantId(firstVariant);
  if (firstCommerceVariantId) {
    selectedVariantId = firstCommerceVariantId;
    setProductFormVariantId(firstCommerceVariantId);
  }

  setBundleAddButtonState(activeState.isAvailable);
  syncStickyBundleSummary(activeState, offerSelector);
  syncMainBundleButtonPrice(activeState, offerSelector);

  if (announce) {
    const pairLabel = selectedQuantity === 1 ? 'pair' : 'pairs';
    const message = activeState.isAvailable
      ? `${selectedQuantity} ${pairLabel}${activeState.balmUpsell?.selected ? ' + Miracle Balm' : ''} selected. Total ${formatMoney(activeState.total, offerSelector)}.`
      : `Check the color and size for all ${selectedQuantity} ${pairLabel} before adding to cart.`;
    setBundleLiveStatus(message, !activeState.isAvailable);
  }
}

function selectOfferQuantity(event) {
  if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-compression-quantity-input]')) {
    return;
  }

  const option = event.target.closest('[data-compression-quantity]');
  if (!(option instanceof HTMLElement) || !option.closest('[data-compression-offer-selector]')) return;
  if (option.closest('.shopify-section') !== getProductSection()) return;

  const quantity = Number(option.dataset.compressionQuantity);
  if (!Number.isInteger(quantity) || quantity < 1 || event.target.disabled) return;

  selectedQuantity = quantity;
  syncOfferSelector({ announce: true });
}

function selectPairOption(event) {
  if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-compression-pair-option]')) {
    return;
  }

  const offerSelector = event.target.closest('[data-compression-offer-selector]');
  const row = event.target.closest('[data-compression-pair]');
  if (offerSelector !== getOfferSelector() || !(row instanceof HTMLElement)) return;

  syncColorPicker(event.target);
  row.dataset.userSelected = 'true';
  offerSelector.dataset.bundleDirty = 'true';
  syncOfferSelector({ announce: true });
  syncPrimaryPairToNativeVariant(row);

  if (row.dataset.pairIndex === '0' && event.target.matches('[data-compression-pair-color]')) {
    scheduleVariantMediaFocus(event.target.value);
  }
}

function claimBundleProductForms(productSection = getProductSection()) {
  if (!productSection?.querySelector('[data-compression-offer-selector]')) return;

  productSection.querySelectorAll('product-form-component').forEach((component) => {
    if (!(component instanceof HTMLElement) || component.dataset.compressionSubmitClaimed === 'true') return;

    const nativeSubmitAction = component.getAttribute('on:submit');
    if (nativeSubmitAction === null) return;

    component.dataset.compressionSubmitClaimed = 'true';
    component.dataset.compressionNativeSubmit = nativeSubmitAction;
    component.removeAttribute('on:submit');
  });
}

function releaseBundleProductForms(productSection = getProductSection()) {
  productSection?.querySelectorAll('[data-compression-submit-claimed="true"]').forEach((component) => {
    if (!(component instanceof HTMLElement)) return;

    const nativeSubmitAction = component.dataset.compressionNativeSubmit;
    if (nativeSubmitAction) component.setAttribute('on:submit', nativeSubmitAction);
    delete component.dataset.compressionSubmitClaimed;
    delete component.dataset.compressionNativeSubmit;
  });
}

function getBundleCartItems() {
  const activeState = getActiveBundleState();
  const rows = activeState.bundle
    ? Array.from(activeState.bundle.querySelectorAll('[data-compression-pair]'))
    : [];

  if (rows.length !== selectedQuantity) {
    return { items: [], invalidControl: undefined, error: 'Choose a valid bundle before adding to cart.' };
  }

  const groupedItems = new Map();
  let invalidControl;

  rows.forEach((row) => {
    const variant = resolvePairVariant(row);
    const commerceVariantId = getCommerceVariantId(variant);
    if (!commerceVariantId || !variant?.available) {
      invalidControl ||= getPairSelects(row)[0];
      return;
    }

    const variantId = String(commerceVariantId);
    groupedItems.set(variantId, (groupedItems.get(variantId) || 0) + 1);
  });

  if (invalidControl || groupedItems.size === 0) {
    return {
      items: [],
      invalidControl,
      error: 'One or more selected color and size combinations are unavailable. Please choose another option.',
    };
  }

  const items = Array.from(groupedItems, ([variantId, quantity]) => ({ variantId, quantity }));
  const giftPickers = isBambooGiftOfferActive()
    ? Array.from(activeState.bundle.querySelectorAll('[data-compression-gift]')) : [];
  for (const [giftIndex, giftPicker] of giftPickers.entries()) {
    const giftVariant = resolveBundleGift(giftPicker);
    const giftVariantId = getCommerceVariantId(giftVariant);
    if (!giftVariantId || !giftVariant?.available) {
      return { items: [], invalidControl: giftPicker.querySelector('select'), error: 'Choose an available colour and size for your free Bamboo pair.' };
    }
    items.push({
      variantId: String(giftVariantId), quantity: 1,
      properties: {
        _skin_elite_bamboo_gift: 'v1', _skin_elite_bamboo_gift_slot: String(giftIndex + 1),
        'Bundle bonus': 'Extra pair included',
      },
    });
  }
  if (activeState.balmUpsell?.selected) {
    items.push({
      variantId: activeState.balmUpsell.variantId, quantity: 1,
      properties: {
        _skin_elite_bamboo_upsell: 'miracle-balm',
        _skin_elite_source_variant_id: activeState.balmUpsell.sourceVariantId,
      },
    });
  }
  return { items, invalidControl: undefined, error: undefined };
}

function setProductFormMessage(form, message, { error = false } = {}) {
  const productForm = form?.closest('product-form-component');
  const liveRegion = productForm?.querySelector('[ref="liveRegion"]');
  const errorContainer = productForm?.querySelector('[ref="addToCartTextError"]');

  if (liveRegion) {
    liveRegion.setAttribute('aria-live', error ? 'assertive' : 'polite');
    setTextContent(liveRegion, message);
  }

  if (!errorContainer) return;

  let errorMessage = errorContainer.querySelector('[data-compression-error-message]');
  if (!errorMessage && error) {
    errorMessage = document.createElement('span');
    errorMessage.setAttribute('data-compression-error-message', '');
    errorContainer.append(errorMessage);
  }

  if (errorMessage) setTextContent(errorMessage, error ? message : '');
  errorContainer.classList.toggle('hidden', !error);
  if (error) errorContainer.setAttribute('aria-live', 'assertive');
  else errorContainer.removeAttribute('aria-live');
}

function setBundleBusy(isBusy) {
  bundleAddInProgress = isBusy;
  const offerSelector = getOfferSelector();
  if (offerSelector) offerSelector.dataset.busy = String(isBusy);
  setBundleAddButtonState(isBusy ? false : getActiveBundleState().isAvailable);
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : undefined))
    .filter((sectionId, index, sectionIds) => Boolean(sectionId) && sectionIds.indexOf(sectionId) === index);
}

async function fetchCurrentCart() {
  const cartItemsComponent = document.querySelector('cart-items-component');
  if (cartItemsComponent) {
    await customElements.whenDefined('cart-items-component');
    if (typeof cartItemsComponent.fetchCartData === 'function') return cartItemsComponent.fetchCartData();
  }

  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to refresh cart (${response.status}).`);
  return response.json();
}

function resolveCartErrorMessage(payload, response) {
  if (payload?.description) return String(payload.description);
  if (payload?.message) return String(payload.message);
  if (typeof payload?.errors === 'string') return payload.errors;
  return `Unable to add this bundle to cart${response?.status ? ` (${response.status})` : ''}. Please try again.`;
}

async function settleFailedCartEvent(deferredEventPromise, detail) {
  try {
    const ajaxCart = await fetchCurrentCart();
    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        ...detail,
        didError: true,
        items: ajaxCart.items,
      },
    });
  } catch (error) {
    deferredEventPromise.reject(error);
  }
}

async function addSelectedBundleToCart(form) {
  if (!(form instanceof HTMLFormElement) || bundleAddInProgress) return;

  syncOfferSelector();
  const selection = getBundleCartItems();
  if (selection.error) {
    setProductFormMessage(form, selection.error, { error: true });
    setBundleLiveStatus(selection.error, true);
    form.closest('product-form-component')?.dispatchEvent(
      new CartErrorEvent({
        error: selection.error,
        code: 'INVALID',
      })
    );
    selection.invalidControl?.focus({ preventScroll: false });
    return;
  }

  setBundleBusy(true);
  setProductFormMessage(form, 'Adding your bundle to cart…');
  setBundleLiveStatus('Adding your bundle to cart…');

  const productForm = form.closest('product-form-component') || form;
  let totalQuantity = selection.items.reduce((sum, item) => sum + item.quantity, 0);
  const balmQuantity = selection.items.filter((item) => item.properties?._skin_elite_bamboo_upsell === 'miracle-balm')
    .reduce((sum, item) => sum + item.quantity, 0);
  let pairQuantity = totalQuantity - balmQuantity;
  const cartSectionIds = getCartSectionIds();
  const deferredEventPromise = CartLinesUpdateEvent.createPromise();
  const commerceProductId = getCommerceProductId();
  const eventDetail = {
    source: 'compression-bundle',
    sourceId: form.id || `compression-bundle-${commerceProductId || 'product'}`,
    itemCount: totalQuantity,
    productId: commerceProductId,
  };

  productForm.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: selection.items.map((item) => ({
        merchandiseId: item.variantId,
        quantity: item.quantity,
      })),
      promise: deferredEventPromise.promise,
    })
  );

  try {
    let payload;
    const reconciledCart = await synchronizeBambooGift({ mutation: async (signal) => {
    // A queued cart mutation may start after the visitor's offer has expired.
    if (!isBambooGiftOfferActive()) {
      selection.items = selection.items.filter((item) => item.properties?._skin_elite_bamboo_gift !== 'v1');
    }
    const response = await fetch(Theme.routes.cart_add_url, {
      signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: selection.items.map((item) => ({
          id: Number(item.variantId),
          quantity: item.quantity,
          ...(item.properties ? { properties: item.properties } : {}),
        })),
        sections: cartSectionIds.join(','),
      }),
    });
    payload = await response.json().catch(() => ({}));

    if (!response.ok || payload.status) {
      const error = new Error(resolveCartErrorMessage(payload, response));
      error.cartPayload = payload;
      error.cartStatus = response.status;
      throw error;
    }

    return fetchCurrentCart();
    } });
    const ajaxCart = reconciledCart || await fetchCurrentCart();
    // Reconciliation can remove gifts if the deadline passes during the request.
    if (!isBambooGiftOfferActive()) {
      selection.items = selection.items.filter((item) => item.properties?._skin_elite_bamboo_gift !== 'v1');
    }
    totalQuantity = selection.items.reduce((sum, item) => sum + item.quantity, 0);
    pairQuantity = totalQuantity - balmQuantity;
    eventDetail.itemCount = totalQuantity;
    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        ...eventDetail,
        items: ajaxCart.items,
        sections: reconciledCart?.sections || payload.sections,
        didError: false,
      },
    });

    const pairLabel = pairQuantity === 1 ? 'pair' : 'pairs';
    const successMessage = `${pairQuantity} ${pairLabel}${balmQuantity ? ' and 1 Miracle Balm' : ''} added to cart.`;
    setProductFormMessage(form, successMessage);
    setBundleLiveStatus(successMessage);

    window.setTimeout(() => {
      const liveRegion = productForm.querySelector?.('[ref="liveRegion"]');
      if (liveRegion?.textContent.trim() === successMessage) liveRegion.textContent = '';
    }, 5000);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unable to add this bundle to cart. Please try again.';
    const errorCode = error?.cartStatus && error.cartStatus < 500 ? 'INVALID' : 'SERVICE_UNAVAILABLE';

    productForm.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: errorCode,
        detail: {
          errors: error?.cartPayload?.errors,
          description: error?.cartPayload?.description,
        },
      })
    );
    await settleFailedCartEvent(deferredEventPromise, eventDetail);
    setProductFormMessage(form, errorMessage, { error: true });
    setBundleLiveStatus(errorMessage, true);
  } finally {
    setBundleBusy(false);
  }
}

function getSizeDialog() {
  return getProductSection()?.querySelector('[data-compression-size-dialog]');
}

function getSizeSelect() {
  const select = getProductSection()?.querySelector('select[name="options[Size]"]');
  return select instanceof HTMLSelectElement ? select : undefined;
}

function syncFitStatus() {
  const sizeSelect = getSizeSelect();
  const status = getProductSection()?.querySelector('[data-compression-fit-status]');
  if (!sizeSelect || !status) return;

  setTextContent(status, `Size ${sizeSelect.value} selected. Check your ankle and calf measurements in the size chart.`);
  status.classList.remove('is-unverified');
}

function enhanceSizeControl() {
  const sizeSelect = getSizeSelect();
  const sizeOption = sizeSelect?.closest('.variant-option');
  const dialog = getSizeDialog();
  if (!sizeSelect || !sizeOption || !dialog) return;

  const legacyHeader = sizeOption.querySelector('.compression-size-inline-header');
  if (legacyHeader) {
    const label = legacyHeader.querySelector('label');
    if (label) legacyHeader.before(label);
    legacyHeader.remove();
  }

  let status = sizeOption.querySelector('[data-compression-fit-status]');
  if (!status) {
    status = document.createElement('p');
    status.className = 'compression-fit-status';
    status.setAttribute('data-compression-fit-status', '');
    sizeOption.append(status);
  }

  syncFitStatus();
}

function openSizeDialog(trigger) {
  const dialog = getSizeDialog();
  if (!(dialog instanceof HTMLDialogElement)) return;

  sizeDialogReturnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
  const pairSizeSelect = trigger?.closest?.('[data-compression-pair]')?.querySelector('[data-compression-pair-size]');
  sizeDialogTargetSelect = pairSizeSelect instanceof HTMLSelectElement ? pairSizeSelect : getSizeSelect();
  if (!dialog.open) dialog.showModal();
  dialog.querySelector('[data-compression-size-close]')?.focus();
}

function closeSizeDialog() {
  const dialog = getSizeDialog();
  if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
}

function applySizeFromGuide(size) {
  const sizeSelect = sizeDialogTargetSelect?.isConnected ? sizeDialogTargetSelect : getSizeSelect();
  if (!sizeSelect || !Array.from(sizeSelect.options).some((option) => option.value === size)) return;

  sizeSelect.value = size;
  sizeSelect.dispatchEvent(new Event('input', { bubbles: true }));
  sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
  sizeDialogReturnFocus = sizeSelect;
  closeSizeDialog();
  scheduleVariantSync();
}

function handleSizeGuideClick(event) {
  if (!(event.target instanceof Element)) return;
  const inProductSection = event.target.closest('.shopify-section') === getProductSection();
  if (!inProductSection && !isCompressionLandingElement(event.target)) return;

  const dialog = event.target.closest('[data-compression-size-dialog]');
  if (dialog instanceof HTMLDialogElement && event.target === dialog) {
    closeSizeDialog();
    return;
  }

  const openTrigger = event.target.closest('[data-compression-size-open]');
  if (openTrigger) {
    event.preventDefault();
    openSizeDialog(openTrigger);
    return;
  }

  if (event.target.closest('[data-compression-size-close]')) {
    closeSizeDialog();
    return;
  }

  const applyButton = event.target.closest('[data-compression-size-apply]');
  if (applyButton instanceof HTMLElement) {
    applySizeFromGuide(applyButton.dataset.compressionSizeApply);
  }
}

function handleSizeDialogClose(event) {
  if (!(event.target instanceof HTMLDialogElement)) return;
  if (!event.target.matches('[data-compression-size-dialog]')) return;
  if (event.target.closest('.shopify-section') !== getProductSection()) return;

  if (sizeDialogReturnFocus instanceof HTMLElement) {
    sizeDialogReturnFocus.focus({ preventScroll: true });
  }
  sizeDialogReturnFocus = undefined;
  sizeDialogTargetSelect = undefined;
}

function getScopedElements(scope, selector) {
  if (!scope?.querySelectorAll) return [];

  const elements = Array.from(scope.querySelectorAll(selector));
  if (scope instanceof Element && scope.matches(selector)) elements.unshift(scope);
  return elements;
}

function isCompressionLandingElement(element) {
  const landing = element.closest('.compression-landing');
  return Boolean(landing && getMainContent()?.contains(landing));
}

function activateBenefitTab(nextTab, moveFocus = false) {
  if (!(nextTab instanceof HTMLElement) || !isCompressionLandingElement(nextTab)) return;

  const tabGroup = nextTab.closest('[data-compression-benefit-tabs]');
  if (!tabGroup) return;

  const tabs = Array.from(tabGroup.querySelectorAll('[data-compression-benefit-tab]'));
  const panels = Array.from(tabGroup.querySelectorAll('[data-compression-benefit-panel]'));
  const nextKey = nextTab.getAttribute('data-compression-benefit-tab');

  tabs.forEach((tab) => {
    const isActive = tab === nextTab;
    tab.classList.toggle('is-active', isActive);
    tab.setAttribute('aria-selected', String(isActive));
    tab.tabIndex = isActive ? 0 : -1;
  });

  panels.forEach((panel) => {
    const isActive = panel.getAttribute('data-compression-benefit-panel') === nextKey;
    panel.classList.toggle('is-active', isActive);
    panel.hidden = !isActive;
  });

  if (moveFocus) nextTab.focus();
}

function enhanceBenefitTabs(scope = getMainContent()) {
  getScopedElements(scope, '[data-compression-benefit-tabs]').forEach((tabGroup) => {
    if (!isCompressionLandingElement(tabGroup)) return;

    const tabs = Array.from(tabGroup.querySelectorAll('[data-compression-benefit-tab]'));
    const panels = Array.from(tabGroup.querySelectorAll('[data-compression-benefit-panel]'));
    if (!tabs.length || !panels.length) return;

    tabGroup.classList.add('is-benefit-tabs-ready');
    activateBenefitTab(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
  });
}

function handleBenefitTabClick(event) {
  if (!(event.target instanceof Element)) return;

  const tab = event.target.closest('[data-compression-benefit-tab]');
  if (tab instanceof HTMLElement) activateBenefitTab(tab);
}

function handleBenefitTabKeydown(event) {
  if (!(event.target instanceof Element)) return;

  const currentTab = event.target.closest('[data-compression-benefit-tab]');
  if (!(currentTab instanceof HTMLElement) || !isCompressionLandingElement(currentTab)) return;

  const tabGroup = currentTab.closest('[data-compression-benefit-tabs]');
  const tabs = Array.from(tabGroup?.querySelectorAll('[data-compression-benefit-tab]') || []);
  const currentIndex = tabs.indexOf(currentTab);
  if (currentIndex < 0) return;

  let nextIndex;
  if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
  if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  if (event.key === 'Home') nextIndex = 0;
  if (event.key === 'End') nextIndex = tabs.length - 1;
  if (nextIndex === undefined) return;

  event.preventDefault();
  activateBenefitTab(tabs[nextIndex], true);
}

function handleProductScroll(event) {
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest('[data-compression-product-scroll]');
  if (!(trigger instanceof HTMLElement) || !isCompressionLandingElement(trigger)) return;

  const productSection = getProductSection();
  if (!productSection) return;

  event.preventDefault();
  const target = productSection.querySelector('.product-details') || productSection;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
}

function syncStickyVisibility() {
  const productSection = getProductSection();
  const stickyCart = productSection?.querySelector('sticky-add-to-cart');
  const stickyBar = stickyCart?.querySelector('.sticky-add-to-cart__bar');
  const buyButton = productSection?.querySelector(
    'product-form-component form[data-type="add-to-cart-form"] .add-to-cart-button'
  );

  if (
    !(stickyCart instanceof HTMLElement) ||
    !(stickyBar instanceof HTMLElement) ||
    !(buyButton instanceof HTMLElement)
  ) {
    return;
  }

  const headerBottom = document.querySelector('#header-group')?.getBoundingClientRect().bottom || 0;
  const hasPassedBuyButton = buyButton.getBoundingClientRect().bottom <= headerBottom + 8;
  const footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
  const footerRect = footer?.getBoundingClientRect();
  const isFooterVisible = Boolean(
    footerRect && footerRect.top < window.innerHeight && footerRect.bottom > 0
  );
  const chat = document.querySelector('shopify-chat');
  const isChatOpen =
    chat instanceof HTMLElement &&
    (chat.hasAttribute('open') || Boolean(chat.closest('theme-drawer')?.hasAttribute('open')));
  const shouldShowSticky =
    hasPassedBuyButton &&
    stickyCart.dataset.variantAvailable === 'true' &&
    !isFooterVisible &&
    !isChatOpen;

  stickyCart.classList.toggle('is-compression-past-buy', shouldShowSticky);
  stickyBar.dataset.stuck = String(shouldShowSticky);
}

function scheduleStickyVisibilitySync() {
  if (stickyVisibilityFrame) return;

  stickyVisibilityFrame = requestAnimationFrame(() => {
    stickyVisibilityFrame = undefined;
    syncStickyVisibility();
  });
}

function clearScheduledProductWork() {
  if (productUpdateFrame) cancelAnimationFrame(productUpdateFrame);
  if (variantSyncFrame) cancelAnimationFrame(variantSyncFrame);
  if (stickyVisibilityFrame) cancelAnimationFrame(stickyVisibilityFrame);
  productUpdateFrame = undefined;
  variantSyncFrame = undefined;
  stickyVisibilityFrame = undefined;

  variantSyncTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
  variantSyncTimeouts.clear();
  primaryMediaRestoreTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
  primaryMediaRestoreTimeouts.clear();
  variantMediaFocusTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
  variantMediaFocusTimeouts.clear();
}

function resetProductState() {
  selectedVariantId = undefined;
  variantMap = undefined;
  selectedQuantity = 4;
  sizeDialogReturnFocus = undefined;
  sizeDialogTargetSelect = undefined;
  bundleAddInProgress = false;
  initializedOfferSelector = undefined;
  initializedBalmToggle = undefined;
  primaryMediaRestoreScheduled = false;
}

function teardownProductSection() {
  productSectionObserver?.disconnect();
  productSectionObserver = undefined;
  clearScheduledProductWork();
  releaseBundleProductForms(activeProductSection);
  activeProductSection = undefined;
  resetProductState();
}

function observeProductSection(productSection) {
  if (productSectionObserver) return;

  productSectionObserver = new MutationObserver(() => {
    // Reclaim a product form immediately if Horizon replaces it during a
    // variant/section morph, before the next possible user interaction.
    claimBundleProductForms(productSection);
    if (productUpdateFrame) return;

    productUpdateFrame = requestAnimationFrame(() => {
      productUpdateFrame = undefined;
      if (activeProductSection !== productSection || !productSection.isConnected) return;

      syncResolvedVariantState();
      applyFriendlyVariantLabels();
      enhanceSizeControl();
      syncFitStatus();
      syncStickyVisibility();
    });
  });

  productSectionObserver.observe(productSection, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

function initializeProductSection(scope = getMainContent()) {
  const scopedProductSection = findProductSection(scope);
  const productSection =
    scopedProductSection || (activeProductSection?.isConnected ? activeProductSection : findProductSection());

  if (!productSection) return;

  if (activeProductSection !== productSection) {
    teardownProductSection();
    activeProductSection = productSection;
  }

  productSection.classList.add('compression-product-section', 'is-compression-enhanced');
  claimBundleProductForms(productSection);
  syncInitialProductVariant();
  syncOfferSelector();
  syncInitialOfferVariantToNative();
  applyFriendlyVariantLabels();
  enhanceSizeControl();
  syncFitStatus();
  syncStickyVisibility();
  scheduleInitialPrimaryMediaRestore();
  scheduleVariantSync();
  observeProductSection(productSection);
}

function handleShopifySectionLoad(event) {
  const scope = event.target instanceof Element ? event.target : getMainContent();
  initializeProductSection(scope);
  enhanceBenefitTabs(scope);
  scheduleReviewWidgetSync();
}

function handleShopifySectionUnload(event) {
  if (!(event.target instanceof Element)) return;
  if (!activeProductSection) return;
  if (event.target === activeProductSection || event.target.contains(activeProductSection)) {
    teardownProductSection();
  }
}

const lifecycleKey = Symbol.for('skin-elite.compressionLandingLifecycle');
globalThis[lifecycleKey]?.destroy?.();

const eventController = new AbortController();
const signal = eventController.signal;

document.addEventListener('shopify:product:select', syncSelectedProductVariant, { signal });
document.addEventListener('submit', handleBundleProductSubmit, { capture: true, signal });
document.addEventListener('click', handleBundleAddClick, { capture: true, signal });
document.addEventListener('change', syncVariantControlChange, { signal });
document.addEventListener('change', selectOfferQuantity, { signal });
document.addEventListener('change', selectPairOption, { signal });
document.addEventListener('click', handleSizeGuideClick, { signal });
document.addEventListener('close', handleSizeDialogClose, { capture: true, signal });
document.addEventListener('click', handleBenefitTabClick, { signal });
document.addEventListener('click', handleColorPickerClick, { signal });
document.addEventListener('keydown', handleBenefitTabKeydown, { signal });
document.addEventListener('keydown', handleColorPickerKeydown, { signal });
document.addEventListener('click', handleProductScroll, { signal });
document.addEventListener('click', scheduleStickyVisibilitySync, { signal });
document.addEventListener('shopify:section:load', handleShopifySectionLoad, { signal });
document.addEventListener('shopify:section:unload', handleShopifySectionUnload, { signal });
document.querySelector('.page-wrapper')?.addEventListener('scroll', scheduleStickyVisibilitySync, {
  passive: true,
  signal,
});
window.addEventListener('resize', scheduleStickyVisibilitySync, { passive: true, signal });
window.addEventListener('scroll', scheduleStickyVisibilitySync, { passive: true, signal });
window.addEventListener('pageshow', scheduleCompressionLandingHashScroll, { signal });
window.addEventListener('hashchange', scheduleCompressionLandingHashScroll, { signal });
window.addEventListener('skin-elite:countdown-state', (event) => {
  if (event.detail?.key === 'skin-elite:halloween:bamboo:20261022:hms-v2') syncOfferSelector();
}, { signal });
window.addEventListener('pageshow', () => syncOfferSelector(), { signal });
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') syncOfferSelector();
}, { signal });

globalThis[lifecycleKey] = {
  destroy() {
    eventController.abort();
    if (hashScrollFrame) window.cancelAnimationFrame(hashScrollFrame);
    if (hashScrollTimeout) window.clearTimeout(hashScrollTimeout);
    teardownProductSection();
    teardownReviewWidget();
  },
};

initializeProductSection();
enhanceBenefitTabs();
observeReviewWidget();
scheduleCompressionLandingHashScroll();

document.addEventListener('change', (event) => {
  if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-compression-gift-option]')) return;
  if (event.target.closest('[data-compression-offer-selector]') !== getOfferSelector()) return;
  const picker = event.target.closest('[data-compression-gift]');
  const swatch = picker?.querySelector('[data-compression-gift-swatch]');
  const colour = picker?.querySelector('[data-compression-gift-color]')?.value;
  if (swatch && colour) swatch.dataset.colorValue = colour;
  syncOfferSelector({ announce: true });
});

document.addEventListener('change', (event) => {
  if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-compression-balm-toggle]')) return;
  if (event.target.closest('[data-compression-offer-selector]') !== getOfferSelector()) return;
  balmUpsellSelected = event.target.checked && !event.target.disabled;
  syncOfferSelector({ announce: true });
});
